<script>
  import Header from "../components/Header.svelte";
  import Footer from "../components/Footer.svelte";
  import { onMount } from "svelte";
  import LandingHeader from "../components/LandingHeader.svelte";

  // Scroll to the top when the component is mounted
  onMount(() => {
    window.scrollTo(0, 0);
  });
</script>

<LandingHeader />
<br /><br />

<body>
  <section class="privacy-policy">
    <h1>Privacy Policy</h1>
    <p class="last-updated">Last Updated: November 1, 2024</p>

    <p>
      Welcome to TruLens, a digital media verification solution provided by
      Jionex IT Services Co. L.L.C (“Jionex,” “we,” “us,” or “our”). TruLens is
      dedicated to empowering users to verify the source, authenticity, and
      context of digital media, protecting against misrepresentation, tampering,
      and misinformation. This Privacy Policy explains how we collect, use, and
      share information when you use TruLens services, ensuring transparency and
      privacy across our platform.
    </p>

    <h2>Scope of this Privacy Policy</h2>
    <p>
      This Privacy Policy applies to all users of TruLens, including website
      visitors, business clients, and end-users of our verification tools. By
      accessing or using TruLens, you agree to this Privacy Policy’s terms.
    </p>

    <h2>Information We Collect</h2>
    <p>We may collect the following types of information:</p>

    <h3>Personal Information</h3>
    <p>
      This includes any data that identifies you as an individual or business
      entity, such as:
    </p>
    <ul>
      <li>
        <strong>Identity Information:</strong> Name, email address,
        and other identification data provided during account setup.
      </li>
      <li>
        <strong>Verification Media:</strong> Images, videos, audio files, and associated
        metadata (e.g., time, date, location, and device details) used for verification
        processes.
      </li>
    </ul>

    <h3>Technical Information</h3>
    <p>
      We collect information about your interactions with our services,
      including:
    </p>
    <ul>
      <li>
        <strong>Device and Usage Data:</strong> browser type and version,
        operating system, device type, and usage patterns on TruLens.
      </li>
      <li>
        <strong>Authentication and Security Data:</strong> Data related to the secure
        upload and access of media files, including two-factor authentication, biometric
        access (when enabled), and session details.
      </li>
    </ul>

    <h2>How We Use Your Information</h2>
    <p>TruLens uses collected information to:</p>
    <ul>
      <li>
        <strong>Provide Verification Services:</strong> Authenticate and verify digital
        media files, ensuring accurate timestamps, device origin, and location data.
      </li>
      <li>
        <strong>Enhance Platform Security:</strong> Detect, prevent, and respond
        to unauthorized access, fraud, and other security issues.
      </li>
      <li>
        <strong>Improve and Personalize Services:</strong> Tailor platform functionality,
        user experience, and communications based on user preferences and feedback.
      </li>
      <li>
        <strong>Compliance and Legal:</strong> Meet legal and regulatory requirements,
        including standards for secure and traceable media (e.g., C2PA compliance).
      </li>
    </ul>

    <!-- <h2>Cookies and Tracking Technologies</h2>
    <p>
      TruLens uses cookies and similar tracking technologies to provide a
      seamless experience and enhance functionality:
    </p>
    <ul>
      <li>
        <strong>Essential Cookies:</strong> Necessary for secure access and maintaining
        user sessions on the platform.
      </li>
      <li>
        <strong>Analytics Cookies:</strong> Used to collect aggregated data to monitor
        and improve platform performance.
      </li>
      <li>
        <strong>Functionality Cookies:</strong> Store user preferences and settings
        for personalized experiences.
      </li>
    </ul>
    <p>
      You can adjust browser settings to decline cookies, though certain
      features may be limited without them.
    </p> -->

    <h2>Information Sharing and Disclosure</h2>
    <p>We only share your information under the following circumstances:</p>
    <ul>
      <li>
        <strong>Service Providers:</strong> With trusted third-party providers for
        payment processing, technical support, and secure storage. These providers
        are bound by confidentiality obligations and may only use your information
        to deliver services on our behalf.
      </li>
      <li>
        <strong>Legal and Regulatory:</strong> To comply with applicable laws, legal
        processes, or regulatory requirements.
      </li>
      <li>
        <strong>Business Transfers:</strong> In the event of a merger, acquisition,
        or sale of TruLens, your information may be transferred as part of the business
        assets.
      </li>
    </ul>

    <h2>Data Security</h2>
    <p>
      TruLens employs strict security protocols, including encryption and access
      controls, to protect user data. Although we strive for complete security,
      no electronic storage or transmission method can guarantee absolute
      security. Users are encouraged to keep their login credentials
      confidential.
    </p>

    <h2>Your Rights and Choices</h2>
    <p>
      Depending on your location, you may have the following rights concerning
      your personal data:
    </p>
    <ul>
      <li>
        <strong>Access:</strong> Request a copy of the personal information we hold.
      </li>
      <li>
        <strong>Correction:</strong> Request corrections to inaccurate or incomplete
        data.
      </li>
      <li>
        <strong>Deletion:</strong> Request deletion of personal data where appropriate.
      </li>
      <li>
        <strong>Restriction and Objection:</strong> Request limitations on data processing
        or object to certain uses.
      </li>
      <li>
        <strong>Data Portability:</strong> Request a transfer of data to another
        service provider.
      </li>
    </ul>
    <p>Please contact us at admin@trulens.ae to exercise these rights.</p>

    <h2>International Data Transfers</h2>
    <p>
      TruLens may process and store your data in different locations around the
      world. We ensure that all data transfers comply with local data protection
      laws and standards, particularly in regions with stricter privacy
      regulations.
    </p>

    <h2>Data Retention</h2>
    <p>
      We retain personal information only as long as necessary for verification,
      security, or legal compliance. Once data is no longer needed, we securely
      delete or anonymize it.
    </p>

    <h2>Policy for Minors</h2>
    <p>
      TruLens services are intended for users over 18. We do not knowingly
      collect data from minors. If we discover information from a minor under
      18, it will be promptly deleted.
    </p>

    <h2>Changes to This Privacy Policy</h2>
    <p>
      We may update this Privacy Policy periodically to reflect changes in our
      practices, technologies, or regulatory requirements. Changes will be
      posted with a revised “Last Updated” date. Continued use of TruLens
      following such changes constitutes acceptance of the revised policy.
    </p>

    <h2>Contact Information</h2>
    <p>
      If you have questions or concerns regarding this Privacy Policy or your
      personal information, please contact us at:
    </p>
    <p>
      <strong>Jionex IT Services Co. L.L.C</strong><br />
      Head Office: M5, Liberty Building, 18th Service Road, Al Garhoud, Dubai, U.A.E<br
      />
      Email: admin@trulens.ae || contact@jionex.com
    </p>
  </section>
</body>

<br /><br />

<Footer />

<style>
  * {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
    color: #333;
  }

  .privacy-policy {
    max-width: 95%;
    margin: auto;
    padding-top: 0 !important;
    padding: 20px;
    line-height: 1.6;
  }

  h1 {
    font-size: 3em;
    color: #003366;
    margin-bottom: 10px;
    text-align: start;
  }

  .last-updated {
    font-size: 1em;
    color: #666;
    text-align: start;
    margin-bottom: 20px;
  }

  h2 {
    font-size: 1.5em;
    color: #003366;
    margin-top: 20px;
    margin-bottom: 10px;
  }

  h3 {
    font-size: 1.25em;
    color: #004080;
    margin-top: 15px;
    margin-bottom: 5px;
  }

  ul {
    padding-left: 20px;
    margin-top: 10px;
  }

  ul li {
    margin-bottom: 8px;
  }

  p {
    font-size: 1em;
    margin-top: 8px;
    margin-bottom: 8px;
  }

  /* Media Queries */
  @media (max-width: 768px) {
    h1 {
      font-size: 1.8em;
    }

    h2 {
      font-size: 1.4em;
    }

    h3 {
      font-size: 1.2em;
    }
  }

  @media (max-width: 480px) {
    h1 {
      font-size: 1.6em;
    }

    h2 {
      font-size: 1.2em;
    }

    h3 {
      font-size: 1em;
    }

    .privacy-policy {
      padding: 10px;
    }
  }

  @media (max-width: 390px) {
    h1 {
      font-size: 1.5em;
    }

    h2 {
      font-size: 1.1em;
    }

    h3 {
      font-size: 0.9em;
    }

    .privacy-policy {
      padding: 8px;
    }
  }
</style>
